import Vue from 'vue';
import { mapGetters } from 'vuex';
import path from 'path';
import _ from 'lodash';
import Cropper from 'cropperjs';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';

export default Vue.component('profile-pic', {
  template: require('./template.html'),
  name: 'profile-pic',
  mixins: [guid, uploader],
  components: {
    uploadProgress,
  },
  computed: {
    ...mapGetters(['currentUser', 'userAttributes']),
  },
  data() {
    return {
      loading: false,
      userLoading: false,
      cachedPic: '',
      cachedPicBucketPath: '',
      newPic: null,
      newUrl: '',
      ers: [],
      saveError: false,
      cropper: null,
      openCropper: false,
    };
  },
  props: ['toggle-modal', 'modal'],
  watch: {
    modal(o) {
      if (o) {
        this.cachedPic = this.currentUser.pic;
        this.cachedPicBucketPath = this.getBucketPath(this.cachedPic);
      } else {
        this.cachedPic = '';
      }
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: 1 / 1,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
    },
  },
  methods: {
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    destroyCropper() {
      this.openCropper = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadCropped() {
      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 96,
          height: 96,
          imageSmoothingQuality: 'high',
        });
        const blob = await this.getBlob(canvas);
        // send off to our s3 upload mixin
        const pic = await this.uploadFile({
          file: this.newPic,
          body: blob,
        });
        this.newUrl = pic.Location;
        this.$store.dispatch('setUserProfilePic', {
          pic: pic.Location,
        });
        this.destroyCropper();
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'profile pic uploadCropped',
          });
        }
      } finally {
        if (this.showFiles) {
          setTimeout(() => {
            this.fetchImageFiles();
          }, 500);
        }
      }
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // gather naming information
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `drz_profile_pic_${_.trim(fileName)}`;
              fileName = _.replace(fileName, /\s+/g, '_');
              fileName = `admin/user-profile-pics/${fileName}`;
              this.newPic = fileName;
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'profile pic openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    async revertPic() {
      // only do a revert if the image has changed
      if (this.cachedPic !== this.currentUser.pic) {
        this.userLoading = true;
        this.$store.dispatch('setUserProfilePic', {
          pic: this.cachedPic,
        });
        // delete newly updated image from s3 if not gonna use it
        try {
          const del = await this._s3deleteFile({
            key: `${this._customerFolder}/${this.newPic}`,
          });
          this.$log.debug('Successfully deleted file: ', del);
        } catch (e) {
          this.$log.error('error: ', e);
          await this._reportError({
            error: e,
            fn: 'revertPic',
          });
        } finally {
          this.userLoading = false;
        }
      }
      this.closeModal('profilePic');
    },
    async saveProfilePic() {
      this.userLoading = true;
      this.saveError = false;
      try {
        await this._cognitoUpdateAttrs({
          attrs: [
            {
              Name: 'picture',
              Value: this.newUrl,
            },
          ],
        });
        // if subuser, update owners data
        if (this.currentUser.subuser) {
          await this._SEND([{
            name: 'updateSubUserAttrs',
            payload: { attr: 'pic', value: this.newUrl },
          }]);
        }
        this.closeModal('profilePic');
      } catch (e) {
        this.saveError = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'saveProfilePic',
        });
      } finally {
        this.userLoading = false;
      }
    },
    getBucketPath(url) {
      let path = document.createElement('a');
      path.href = url;
      path = path.pathname.split('admin/')[1];
      return `admin/${path}`;
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    closeModal(type) {
      this.$emit('toggle-modal', type, 'child');
    },
    formatErrors(arr) {
      return arr.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/account/profile/pic-modal/index.js